import {
	ACESFilmicToneMapping,
	BasicShadowMap,
	LinearToneMapping,
	PCFSoftShadowMap,
	VSMShadowMap,
	WebGLRenderer
} from 'three';

/**
 * Quality settings returned by getQualitySettings
 */
export interface QualitySettings {
	pixelRatio: number;
	antialias: boolean;
	shadowMapEnabled: boolean;
	shadowMapType: typeof BasicShadowMap | typeof PCFSoftShadowMap | typeof VSMShadowMap;
	toneMapping: typeof LinearToneMapping | typeof ACESFilmicToneMapping;
	enableEnvironmentMap: boolean;
	wireframe: boolean;
	/** Enable logarithmic depth buffer for better depth precision at high quality */
	logarithmicDepthBuffer: boolean;
	/** Camera near plane distance - higher values improve depth buffer precision */
	cameraNear: number;
}

/**
 * Calculate rendering quality settings based on quality level (-10 to 10)
 *
 * @param quality - Quality level:
 *   - 0: Wireframe only + debug mode
 *   - -1 to -10: Same as 1-10 (absolute value) but with debug mode enabled
 *   - 1-10: Normal quality levels
 * @returns Object with quality-dependent rendering settings
 */
export const getQualitySettings = (quality: number): QualitySettings => {
	// Handle wireframe mode (quality 0)
	if (quality === 0) {
		// Wireframe mode - absolute fastest, for debugging
		return {
			pixelRatio: 1,
			antialias: false,
			shadowMapEnabled: false,
			shadowMapType: BasicShadowMap,
			toneMapping: LinearToneMapping,
			enableEnvironmentMap: false,
			wireframe: true,
			logarithmicDepthBuffer: false,
			cameraNear: 0.1
		};
	}

	// For negative values, use absolute value to determine quality level
	// Negative values enable debug mode but use the same quality settings as positive values
	const actualQuality = Math.abs(quality);
	const q = Math.max(1, Math.min(10, actualQuality));

	// Quality levels scale from 1 (lowest) to 10 (highest):
	// 1-3: Low (performance priority)
	// 4-6: Medium (balanced)
	// 7-10: High (quality priority)

	if (q <= 3) {
		// Low quality (1-3) - maximum performance
		return {
			pixelRatio: 1,
			antialias: false,
			shadowMapEnabled: false,
			shadowMapType: BasicShadowMap,
			toneMapping: LinearToneMapping,
			enableEnvironmentMap: false,
			wireframe: false,
			logarithmicDepthBuffer: false,
			cameraNear: 0.1
		};
	} else if (q <= 6) {
		// Medium quality (4-6) - balanced
		// Scale pixel ratio: 1.25 for q=4, 1.375 for q=5, 1.5 for q=6
		const pixelRatioScale = 1 + ((q - 3) * 0.25);
		return {
			pixelRatio: Math.min(window.devicePixelRatio, pixelRatioScale),
			antialias: true,
			shadowMapEnabled: true,
			shadowMapType: PCFSoftShadowMap,
			toneMapping: ACESFilmicToneMapping,
			enableEnvironmentMap: q >= 5,
			wireframe: false,
			logarithmicDepthBuffer: false,
			cameraNear: 0.5
		};
	}

	// High quality (7-10) - best visuals
	// Scale pixel ratio: 1.75 for q=7, 2.0 for q=8, 2.25 for q=9, full devicePixelRatio for q=10
	const pixelRatioScale = q === 10
		? window.devicePixelRatio
		: Math.min(window.devicePixelRatio, 1.5 + ((q - 6) * 0.25));

	// Quality 10 uses PCFSoftShadowMap instead of VSMShadowMap to avoid VSM light bleeding artifacts
	// VSMShadowMap can cause visible streaking on large surfaces like floors
	const shadowType = q === 10 ? PCFSoftShadowMap : VSMShadowMap;

	return {
		pixelRatio: pixelRatioScale,
		antialias: true,
		shadowMapEnabled: true,
		shadowMapType: shadowType,
		toneMapping: ACESFilmicToneMapping,
		enableEnvironmentMap: true,
		wireframe: false,
		// Enable logarithmic depth buffer for quality 8-10 to prevent z-fighting artifacts
		logarithmicDepthBuffer: q >= 8,
		// Larger near plane at high quality improves depth buffer precision
		cameraNear: 1.0
	};
};

/**
 * Calculate anisotropy level based on render quality
 *
 * @param quality - Quality level (0-10)
 * @param renderer - WebGLRenderer to get max anisotropy from
 * @returns Anisotropy value to use for textures
 */
export const getAnisotropyForQuality = (quality: number, renderer: WebGLRenderer | null): number => {
	if (!renderer) return 0;

	const maxAnisotropy = renderer.capabilities.getMaxAnisotropy();
	const actualQuality = quality === 0 ? 0 : Math.abs(quality);

	// Scale anisotropy: 0 for q=0-1, 2 for q=2-3, 4 for q=4-5, 8 for q=6-7, max for q=8-10
	if (actualQuality >= 8) {
		return maxAnisotropy;
	} else if (actualQuality >= 6) {
		return Math.min(maxAnisotropy, 8);
	} else if (actualQuality >= 4) {
		return Math.min(maxAnisotropy, 4);
	} else if (actualQuality >= 2) {
		return Math.min(maxAnisotropy, 2);
	}
	return 0;
};

/**
 * Sky texture resolution tiers
 */
export type SkyResolution = 'default' | '2k' | '4k' | '8k' | '16k';

/**
 * Get the preferred sky texture resolution fallback chain for a given quality level.
 *
 * Quality levels map to resolutions:
 * - 0: Wireframe (no texture)
 * - 1: None (no texture)
 * - 2-3: Low (default only)
 * - 4-6: Medium (2k → default)
 * - 7-8: High (4k → 2k → default)
 * - 9: Very-High (8k → 4k → 2k → default)
 * - 10: Ultra-High (16k → 8k → 4k → 2k → default)
 *
 * @param quality - Quality level (0-10)
 * @returns Array of resolutions in preference order (first available wins)
 */
/**
 * Resolve the effective texture-quality tier.
 *
 * `textureQuality` is an independent knob from `renderQuality` so mobile devices
 * can cap sky HDR memory without dropping shader/shadow quality. When it is null
 * or undefined, texture selection follows `renderQuality`.
 *
 * @param textureQuality - Explicit texture tier, or null/undefined to follow renderQuality
 * @param renderQuality  - Fallback render quality (0-10)
 * @returns The quality tier to feed into getSkyResolutionForQuality / getAnisotropyForQuality
 */
export const getEffectiveTextureQuality = (
	textureQuality: number | null | undefined,
	renderQuality: number
): number => (textureQuality === null || textureQuality === undefined ? renderQuality : textureQuality);

export const getSkyResolutionForQuality = (quality: number): SkyResolution[] => {
	const actualQuality = quality === 0 ? 0 : Math.abs(quality);

	if (actualQuality <= 1) return [];                              // No texture
	if (actualQuality <= 3) return ['default'];                     // Low
	if (actualQuality <= 8) return ['2k', 'default'];               // Medium-High
	if (actualQuality === 9) return ['4k', '2k', 'default'];        // Very-High
	return ['8k', '4k', '2k', 'default'];                           // Ultra-High (10)
};

